import { useState, useEffect, useCallback, useRef } from 'react';
import { tokenStore } from '../lib/tokenStore';
import { Bell, Check, AlertTriangle, Clock, X, Dumbbell, Award, CalendarClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ ...tokenStore.getAuthHeaders(), 'Content-Type': 'application/json' });

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications`, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setNotifications(d.notifications || []);
        setUnread(d.unread_count || 0);
      }
    } catch { /* ignore */ }
  }, []);

  // Die Glocke meldet Ereignisse (12.10.2026). Rückstand — überfällige Follow-ups — steht in der Aufgabenliste, nicht hier.
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, { method: 'PATCH', headers: getHeaders() });
    setNotifications(n => n.map(x => x.id === id ? { ...x, read: true } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'PATCH', headers: getHeaders() });
    setNotifications(n => n.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'gerade';
    if (diff < 3600) return `${Math.floor(diff / 60)} Min.`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} Std.`;
    return `${Math.floor(diff / 86400)} T.`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button onClick={() => setOpen(!open)} className="relative p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="bell-btn">
        <Bell className={`w-4.5 h-4.5 ${unread > 0 ? 'text-marke' : 'text-muted-foreground'}`} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-marke text-white rounded-full text-[9px] font-bold flex items-center justify-center animate-pulse" data-testid="unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-2xl z-[60] overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-bold">Benachrichtigungen</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-primary hover:underline font-medium" data-testid="mark-all-read">
                Alle gelesen
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Nichts Neues in den letzten 14 Tagen.</p>
              </div>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border/20 cursor-pointer transition-colors hover:bg-muted/30 ${!n.read ? 'bg-primary/5' : ''}`}
                  onClick={() => {
                    markRead(n.id);
                    const ziel = n.link || { probetraining_gebucht: '/probetrainings', abschluss: '/vertraege', support_ticket: '/support',
                      ernaehrung_coaching: '/ernaehrung', health_alarm: '/settings' }[n.type];
                    if (ziel) { navigate(ziel); setOpen(false); }
                    if (n.type === 'pt_order_open') { navigate('/calendar'); setOpen(false); }
                    if (n.type === 'graduation_ready') { navigate('/graduierung'); setOpen(false); }
                    if (n.type === 'beratung_gebucht' || n.type === 'beratung_abgesagt') { navigate('/kalender'); setOpen(false); }
                    if (n.type === 'reaktivierung_neu' || n.type === 'kuendigungsrisiko') { navigate('/reaktivierung'); setOpen(false); }
                  }}
                  data-testid={`notif-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${n.priority === 'high' ? 'bg-red-100' : n.type === 'pt_order_open' ? 'bg-purple-100 dark:bg-purple-900/30' : n.type === 'graduation_ready' ? 'bg-amber-100 dark:bg-amber-900/30' : (n.type === 'beratung_gebucht' || n.type === 'beratung_abgesagt') ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-muted'}`}>
                    {n.priority === 'high' ? <AlertTriangle className="w-4 h-4 text-red-500" /> :
                      n.type === 'pt_order_open' ? <Dumbbell className="w-4 h-4 text-purple-600 dark:text-purple-400" /> :
                      n.type === 'graduation_ready' ? <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" /> :
                      (n.type === 'beratung_gebucht' || n.type === 'beratung_abgesagt') ? <CalendarClock className="w-4 h-4 text-teal-600 dark:text-teal-400" /> :
                      <Clock className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-snug ${!n.read ? 'font-semibold' : 'text-muted-foreground'}`}>{n.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{n.message}</p>
                    <p className="text-[9px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-marke flex-shrink-0 mt-1.5" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
